import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getArtists,
  getArtist,
  createArtist,
  updateArtist,
  deleteArtist,
  bulkCreateArtists,
  AuthenticationError,
  type Artist,
  type GetArtistsParams,
} from "@/lib/api";
import {
  ArrowUpDown,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader2,
} from "lucide-react";

type SortField = "name" | "created_at" | "updated_at" | "id";
type SortOrder = "asc" | "desc";

export function Artists() {
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [perPage, setPerPage] = useState(10);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form states
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const [formData, setFormData] = useState({ name: "", hidden: false });
  const [bulkNames, setBulkNames] = useState("");
  const [bulkHidden, setBulkHidden] = useState(false);
  const [deletingArtist, setDeletingArtist] = useState<Artist | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to page 1 on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Query for artists list
  const {
    data: artistsData,
    isLoading: loading,
    error: artistsError,
  } = useQuery({
    queryKey: ["artists", page, perPage, debouncedSearch, sortField, sortOrder],
    queryFn: async () => {
      const params: GetArtistsParams = {
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        sort: sortField,
        order: sortOrder,
      };
      return getArtists(params);
    },
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
        return false;
      }
      return failureCount < 1;
    },
  });

  const artists = artistsData?.artists || [];
  const pagination = artistsData?.pagination || {
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  // Mutation for creating artist
  const createMutation = useMutation({
    mutationFn: createArtist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
      setCreateModalOpen(false);
      setFormData({ name: "", hidden: false });
    },
    onError: (error) => {
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
      } else {
        console.error("Failed to create artist:", error);
      }
    },
  });

  // Query for single artist (for editing)
  const { data: editingArtistData } = useQuery({
    queryKey: ["artist", editingArtist?.id],
    queryFn: () => editingArtist ? getArtist(editingArtist.id) : null,
    enabled: !!editingArtist && editModalOpen,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
        return false;
      }
      return failureCount < 1;
    },
  });

  // Update editingArtist when data is fetched
  useEffect(() => {
    if (editingArtistData) {
      setFormData({ name: editingArtistData.name, hidden: editingArtistData.hidden });
    }
  }, [editingArtistData]);

  // Handle edit
  const openEditModal = (artist: Artist) => {
    setEditingArtist(artist);
    setEditModalOpen(true);
  };

  // Mutation for updating artist
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof formData }) =>
      updateArtist(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
      queryClient.invalidateQueries({ queryKey: ["artist", editingArtist?.id] });
      setEditModalOpen(false);
      setEditingArtist(null);
      setFormData({ name: "", hidden: false });
    },
    onError: (error) => {
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
      } else {
        console.error("Failed to update artist:", error);
      }
    },
  });

  // Handle delete
  const openDeleteConfirm = (artist: Artist) => {
    setDeletingArtist(artist);
    setDeleteConfirmOpen(true);
  };

  // Mutation for deleting artist
  const deleteMutation = useMutation({
    mutationFn: deleteArtist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
      setDeleteConfirmOpen(false);
      setDeletingArtist(null);
    },
    onError: (error) => {
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
      } else {
        console.error("Failed to delete artist:", error);
      }
    },
  });

  // Handle bulk create
  const bulkCreateMutation = useMutation({
    mutationFn: bulkCreateArtists,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
      setBulkModalOpen(false);
      setBulkNames("");
      setBulkHidden(false);
      console.log(`Created ${result.created} artists, skipped ${result.skipped}`);
    },
    onError: (error) => {
      if (error instanceof AuthenticationError) {
        logout();
        navigate("/login");
      } else {
        console.error("Failed to bulk create artists:", error);
      }
    },
  });

  const handleCreate = () => {
    if (!formData.name.trim()) return;
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!editingArtist || !formData.name.trim()) return;
    updateMutation.mutate({ id: editingArtist.id, data: formData });
  };

  const handleDelete = () => {
    if (!deletingArtist) return;
    deleteMutation.mutate(deletingArtist.id);
  };

  const handleBulkCreate = () => {
    if (!bulkNames.trim()) return;

    const names = bulkNames
      .split("\n")
      .map((line) => line.split(","))
      .flat()
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    if (names.length === 0) return;

    bulkCreateMutation.mutate({ names, hidden: bulkHidden });
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Sort icon
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUpDown className="ml-1 h-4 w-4" />
    ) : (
      <ArrowUpDown className="ml-1 h-4 w-4 rotate-180" />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Artists</h1>
      </div>

      {/* Search and Toolbar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search artists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setBulkModalOpen(true)} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add Bulk
              </Button>
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Artist
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Artists ({pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : artists.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No artists found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th
                      className="h-12 px-4 text-left align-middle font-medium cursor-pointer hover:bg-accent"
                      onClick={() => handleSort("id")}
                    >
                      <div className="flex items-center">
                        ID
                        <SortIcon field="id" />
                      </div>
                    </th>
                    <th
                      className="h-12 px-4 text-left align-middle font-medium cursor-pointer hover:bg-accent"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center">
                        Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th className="h-12 px-4 text-left align-middle font-medium">
                      Hidden
                    </th>
                    <th
                      className="h-12 px-4 text-left align-middle font-medium cursor-pointer hover:bg-accent"
                      onClick={() => handleSort("created_at")}
                    >
                      <div className="flex items-center">
                        Created At
                        <SortIcon field="created_at" />
                      </div>
                    </th>
                    <th
                      className="h-12 px-4 text-left align-middle font-medium cursor-pointer hover:bg-accent"
                      onClick={() => handleSort("updated_at")}
                    >
                      <div className="flex items-center">
                        Updated At
                        <SortIcon field="updated_at" />
                      </div>
                    </th>
                    <th className="h-12 px-4 text-right align-middle font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {artists.map((artist) => (
                    <tr key={artist.id} className="border-b hover:bg-accent/50">
                      <td className="p-4 align-middle">{artist.id}</td>
                      <td className="p-4 align-middle font-medium">
                        {artist.name}
                      </td>
                      <td className="p-4 align-middle">
                        {artist.hidden ? (
                          <span className="text-destructive">Yes</span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm text-muted-foreground">
                        {formatDate(artist.created_at)}
                      </td>
                      <td className="p-4 align-middle text-sm text-muted-foreground">
                        {formatDate(artist.updated_at)}
                      </td>
                      <td className="p-4 align-middle text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(artist)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteConfirm(artist)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="per-page">Items per page:</Label>
                <Select
                  value={perPage.toString()}
                  onValueChange={(value) => {
                    setPerPage(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="per-page" className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={!pagination.has_prev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((prev) => Math.min(pagination.pages, prev + 1))}
                  disabled={!pagination.has_next}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Artist</DialogTitle>
            <DialogDescription>
              Add a new artist to the database.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Artist name"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="create-hidden"
                checked={formData.hidden}
                onChange={(e) =>
                  setFormData({ ...formData, hidden: e.target.checked })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="create-hidden">Hidden</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateModalOpen(false);
                setFormData({ name: "", hidden: false });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Artist</DialogTitle>
            <DialogDescription>
              Update artist information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Artist name"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-hidden"
                checked={formData.hidden}
                onChange={(e) =>
                  setFormData({ ...formData, hidden: e.target.checked })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-hidden">Hidden</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setEditingArtist(null);
                setFormData({ name: "", hidden: false });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Modal */}
      <Dialog open={bulkModalOpen} onOpenChange={setBulkModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Add Artists</DialogTitle>
            <DialogDescription>
              Add multiple artists at once. Enter one name per line or separate
              with commas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-names">Artist Names</Label>
              <textarea
                id="bulk-names"
                value={bulkNames}
                onChange={(e) => setBulkNames(e.target.value)}
                placeholder="Artist 1&#10;Artist 2&#10;Artist 3"
                className="flex min-h-[200px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="bulk-hidden"
                checked={bulkHidden}
                onChange={(e) => setBulkHidden(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="bulk-hidden">Hidden</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkModalOpen(false);
                setBulkNames("");
                setBulkHidden(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkCreate} disabled={bulkCreateMutation.isPending}>
              {bulkCreateMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Artist</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingArtist?.name}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeletingArtist(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
